import { TelemetryData, isTelemetryDataServerSide } from '@cf-shared/telemetry-shared';
import { uniqBy, remove } from 'lodash-es';
import { LoggingService } from './logging.service';
import { UserDb } from './user-db.service';
import { isDatabaseTornDown } from './database-unavailable-error';
import { WriteOutcome } from './database-wrapper-save-recovery';
import { CloudFunctionsService } from './cloud-functions.service';
import { FlushTelemetryParamsAnonymous } from '@cf-shared/flush-telemetry-interface';

const TELEMETRY_SESSIONSTORAGE_KEY = 'telemetryQueue';
/*
Helper class to hold the telemetry data queue. It synchronizes the data between the in-memory queue, the local storage queue, and the disk queue.
*/
export class TelemetryQueue {
  dataQueue: TelemetryData[] = [];
  analyticsDisabled = true;

  constructor(
    private userDb: UserDb,
    private logging: LoggingService,
    private cloudFunctions: CloudFunctionsService,
  ) {
  }
  initialize(userDb: UserDb, logging: LoggingService) {
    this.userDb = userDb;
    this.logging = logging;
  }

  async initAsync() {
    // get all data from local storage and merge it with the in-memory queue
    let serializedData = sessionStorage.getItem(TELEMETRY_SESSIONSTORAGE_KEY);
    if (serializedData) {
      try {
        let deserializeData = JSON.parse(serializedData);
        this.dataQueue.push(...deserializeData);
      } catch (err) {
        this.logging.trackSilentProblem('Error deserializing telemetry data', { error: err, string: serializedData });
      }
    }

    // get all data from the disk 
    if (this.userDb && this.userDb.isInitialized()) {
      let diskData = await this.userDb.getTelemetryDataAsync();
      if (diskData) {
        // add the client side data to the memory queue
        this.dataQueue.push(...diskData);
      }
    }

    // sort the data by the time
    this.dataQueue.sort((a, b) => a._id.localeCompare(b._id));
    // deduplicate the data
    this.dataQueue = uniqBy(this.dataQueue, '_id');
  }

  // save unsaved events to sessionStorage (temporary storage before disk is ready)
  private updateSessionStorage() {
    // session storage must not be used until the cookies are allowed
    if (this.analyticsDisabled) {
      return;
    }

    // only store events not yet saved to disk (no _rev)
    const unsavedEvents = this.dataQueue.filter(e => e._rev === undefined);

    // if all events are saved to disk, clear sessionStorage
    if (unsavedEvents.length === 0) {
      sessionStorage.removeItem(TELEMETRY_SESSIONSTORAGE_KEY);
      return;
    }

    try {
      sessionStorage.setItem(TELEMETRY_SESSIONSTORAGE_KEY, JSON.stringify(unsavedEvents));
    } catch (err) {
      // QuotaExceeded - clear it, disk is the primary storage anyway
      sessionStorage.removeItem(TELEMETRY_SESSIONSTORAGE_KEY);
    }
  }

  // call this when analytics becomes enabled to persist any accumulated events
  syncToSessionStorage() {
    this.updateSessionStorage();
  }

  // add new data to the queue
  pushData(data: TelemetryData) {
    this.dataQueue.push(data);
    this.updateSessionStorage();
  }

  // remove data from the queue
  async removeDataAsync(data: TelemetryData) {
    // if the data was saved to disk before, delete it from the disk
    if (data._rev && this.userDb && this.userDb.isInitialized()) {
      try {
        // a bulk delete can fall short WITHOUT throwing — a row the recovery could not
        // save — so the catch below does not cover every way this can fail
        // (df-c387b6a15d2d). A write refused outright because the database is shutting
        // down throws, and lands in the catch (df-aa69b381d6d6).
        // The event still leaves the queue either way: it is already on disk, so the
        // next boot reads it back and sends it again, which is the behaviour this had
        // before and is not what is being changed here.
        const notDeleted = await this.userDb.deleteTelemetryAsync([data]);
        if (notDeleted.length > 0) {
          this.logging.trackSilentProblem('telemetry event delete did not land', { docId: data._id });
        }
      } catch (err) {
        this.logging.trackSilentProblem('Error deleting telemetry event', { error: err });
      }
    }
    // remove from memory
    remove(this.dataQueue, data);
    // and from session storage
    this.updateSessionStorage();
  }

  // get reference to oldest data from the queue (without removing it!)
  peekOldestData() {
    return this.dataQueue[0];
  }

  async saveDataToDiskAsync(data: TelemetryData) {
    // if the userDb is not ready yet (user not logged in)
    if (!this.userDb || !this.userDb.isInitialized()) {
      // send the data straight to the server
      try {
        let result = await this.cloudFunctions.flushTelemetry({ allData: [data] } as FlushTelemetryParamsAnonymous);
        if (result && result.allOk) {
          console.log('Telemetry data sent to server directly:', data);
        } else {
          console.error('Telemetry data FAILED to send to server even directly:', data);
        }
        return result?.allOk ?? false;
      } catch (err) {
        console.log('Telemetry: failed to flush to server (server unavailable):', err?.message || err);
        return false;
      }
    }
    try {
      await this.userDb.saveTelemetryEventAsync(data);
      this.updateSessionStorage(); // because the _rev might have been added/changed
      return true;
    } catch (err) {
      // the save now THROWS when the database is being torn down instead of returning as
      // though the event had been stored (df-6ce1f9d6830e). Signing out emits telemetry and
      // tears the database down at the same time, so this branch is reached on an ordinary
      // sign-out. That is our own shutdown, not a fault: answering false is the honest
      // result and the caller keeps the event in session storage. Reporting it would file a
      // Sentry problem on every sign-out and write a `console.error` line that the E2E
      // unexpected-browser-error gate fails on.
      if (isDatabaseTornDown(err)) return false;
      this.logging.trackSilentProblem('Error saving telemetry event', { error: err });
      return false;
    }
  }

  // leave the data to be handled on server side (giving up trying to send it from the client).
  // answers false when the server-side copy was NOT written because the database repair took
  // the save over: then the original stays in the queue, and the caller must stop and retry
  // later rather than move on to the next item (df-7f5d5c076760)
  async delegateDataToServerSideAsync(data: TelemetryData): Promise<boolean> {
    console.log('Delegating telemetry data to server side:', data);
    console.assert(!isTelemetryDataServerSide(data));

    // create a copy of this data as server side, with different id
    let newId = data._id.replace(/^mix\//, 'mix-server/');
    let serverData = { ...data, _id: newId };
    // this is a fresh couch record, so remove helper fields
    delete serverData._rev;
    delete serverData._deleted;

    // save the server-side copy directly (bypass saveDataToDiskAsync which reports all errors to Sentry)
    if (this.userDb && this.userDb.isInitialized()) {
      try {
        const outcome = await this.userDb.saveTelemetryEventAsync(serverData);
        // if the database repair swallowed the save, the copy does not exist and nothing will
        // save it again, so removing the original would lose the item for good. keep the
        // original queued (memory, session storage and disk) and let the caller retry later.
        // the swallowed write itself is already reported by the database wrapper
        if (outcome !== WriteOutcome.Written) {
          console.log(`Telemetry: delegation of ${data._id} deferred, the server-side copy was not written (${outcome})`);
          return false;
        }
        this.updateSessionStorage();
      } catch (err) {
        // saveRecordInternalAsync resolves most conflicts internally (merge + retry).
        // The retry fails when sync updates the doc between fetch and retry —
        // but the doc exists on CouchDB, which is the desired outcome for delegation.
        if (isDatabaseTornDown(err)) {
          // the database is going away underneath us (df-6ce1f9d6830e) — the delegation copy
          // was not written. Our own shutdown, not a fault, and reported nowhere for the same
          // reason as the ordinary save above.
          console.log(`Telemetry: delegation save for ${newId} skipped (database shutting down)`);
        } else if (err?.message?.includes('after conflict retry')) {
          console.log(`Telemetry: delegation save for ${newId} skipped (doc already exists from sync)`);
        } else {
          this.logging.trackSilentProblem('Error saving telemetry event during delegation', { error: err });
        }
      }
    }

    // remove the client side version from disk, memory, and local storage
    await this.removeDataAsync(data);
    return true;
  }

  async saveEntireQueueToDiskAsync(): Promise<{ numSaved: number; }> {
    // if the userDb is not ready, we can't save to disk
    if (!this.userDb || !this.userDb.isInitialized()) {
      return { numSaved: 0 };
    }
    // for each data in the queue
    for (let data of this.dataQueue) {
      // if it is not already saved to disk
      if (data._rev === undefined) {
        // save it to disk
        await this.saveDataToDiskAsync(data);
      }
    }
    return { numSaved: this.dataQueue.length };
  }
}
